import * as Menubar from '@radix-ui/react-menubar';
import { getClockOffset, timeNow } from '../utils/clock';
import { formatTime } from '../utils/dataUtils';
import { openFileJSON, saveFileAs, saveFileJSON } from '../utils/fileHandler';
import { useGlobalShortcuts } from '../utils/useGlobalShortcuts';
import { useMenuBarShortcuts } from '../utils/useMenuBarShortcuts';
import './MenuBar.css';
import {
  $connectedServer,
  $currentFileHandle,
  $date,
  $reminders,
  $serverState,
  $view,
  exportLogbookContext,
  importLogbookContext,
  openNewLogbookWizard,
  setConnectedServer,
} from '@/stores';
import { $tutorial, openDialog } from '@/stores/dialog';
import { logs } from '@/stores/logbook';
import { $scheduledReminders } from '@/stores/scheduledReminders';
import { $dismissedWizard } from '@/stores/ui';
import { ask } from '@/utils/alertUtils';
import { BUILD_PLATFORM, IS_FILE, IS_TAURI } from '@/utils/platformUtils';
import { useStore } from '@nanostores/react';
import { WebviewWindow } from '@tauri-apps/api/webviewWindow';
import { getCurrentWindow } from '@tauri-apps/api/window';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

const twindow = IS_TAURI ? getCurrentWindow() : null;

export function MenuBar() {
  const view = useStore($view);
  const serverState = useStore($serverState);
  const connectedServer = useStore($connectedServer);
  const isRemoteConnected = !serverState && connectedServer !== null;
  const remoteConnectedDisabledProps = isRemoteConnected
    ? {
        disabled: true,
        title: 'Unavailable while connected to a remote server',
      }
    : {};
  const [openMenu, setOpenMenu] = useState<string>('');
  const [showAltHint, setShowAltHint] = useState<boolean>(false);
  const [clockDisplay, setClockDisplay] = useState(() => formatTime(timeNow()));
  const [hasClockOffset, setHasClockOffset] = useState(
    () => getClockOffset() !== 0,
  );

  const newWindow = (url = '/') => {
    switch (BUILD_PLATFORM) {
      case 'tauri': {
        const label = `window-${Date.now()}`;
        const webview = new WebviewWindow(label, {
          url,
        });
        webview.once('tauri://created', function () {
          console.log('webview created');
        });
        webview.once('tauri://error', function (e) {
          console.error('webview error:', e);
        });
        break;
      }
      case 'web': {
        window.open(url, '_blank');
        break;
      }
      case 'file':
        {
          let targetUrl = url;

          if (url === '/') {
            // Open current page in new window
            targetUrl = window.location.href;
          } else if (url === '/officer.html') {
            // Construct path to officer.html in same directory
            const currentUrl = new URL(window.location.href);
            const pathParts = currentUrl.pathname.split('/');
            pathParts[pathParts.length - 1] = 'officer.html';
            currentUrl.pathname = pathParts.join('/');
            targetUrl = currentUrl.href;
          }

          window.open(targetUrl, '_blank');
        }
        break;
    }
  };

  // Action handlers (DRY - reused by both menu items and shortcuts)
  const actions = {
    newFile: async () => {
      if (serverState) return; // Don't allow creating new log when hosting a server
      if (
        !(await ask({
          title: 'New Log',
          message:
            'Are you sure you want to create a new log? Unsaved changes will be lost.',
        }))
      ) {
        return;
      }
      logs.reset();
      setConnectedServer(null);
      $currentFileHandle.set(null);
      $dismissedWizard.set(false);
      $reminders.set([]);
      $scheduledReminders.set([]);
      openNewLogbookWizard();
    },
    open: async () => {
      if (isRemoteConnected) return;
      const result = await openFileJSON();
      if (result) {
        importLogbookContext(result.data);
        $currentFileHandle.set(result.fileHandle);
      }
    },
    save: async () => {
      const data = exportLogbookContext();
      const filename = `logbook_${$date.get() || 'export'}.json`;
      const newHandle = await saveFileJSON(
        data,
        $currentFileHandle.get() ?? undefined,
        filename,
      );
      if (newHandle) {
        $currentFileHandle.set(newHandle);
      }
    },
    saveAs: async () => {
      const data = exportLogbookContext();
      const filename = `logbook_${$date.get() || 'export'}.json`;
      const newHandle = await saveFileAs(data, filename);
      if (newHandle) {
        $currentFileHandle.set(newHandle);
      }
    },
    export: () => {
      openDialog('export');
    },
    exit: () => {
      if (IS_TAURI) {
        twindow?.close();
      } else {
        window.close();
      }
    },
    editReports: () => {
      if (isRemoteConnected) return;
      openDialog('template');
    },
    editTags: () => {
      if (isRemoteConnected) return;
      openDialog('tags');
    },
    editCodewords: () => {
      if (isRemoteConnected) return;
      openDialog('codewords');
    },
    editScheduledReminders: () => {
      if (isRemoteConnected) return;
      openDialog('scheduledReminders');
    },
    editClock: () => {
      openDialog('clockAdjust');
    },
    serverSettings: () => openDialog('serverSettings'),
    connectServer: () => openDialog('connectServer'),
    disconnectServer: () => {
      setConnectedServer(null);
    },
    docs: () => openDialog('docs'),
    about: () => openDialog('about'),
    downloadDesktop: () => openDialog('downloadDesktop'),
    newWindow: () => newWindow(),
    newOfficerWindow: () => newWindow('/officer.html'),
  };

  // Setup Alt+Key shortcuts
  useMenuBarShortcuts({
    f: () => setOpenMenu('file'),
    e: () => setOpenMenu('edit'),
    v: () => setOpenMenu('view'),
    h: () => setOpenMenu('help'),
    t: () => $tutorial.set(true),
  });

  // Setup global keyboard shortcuts (Ctrl+Key combinations)
  useGlobalShortcuts({
    'ctrl+o': actions.open,
    'ctrl+s': actions.save,
    'ctrl+shift+s': actions.saveAs,
    'ctrl+e': actions.export,
    'ctrl+shift+n': actions.newWindow,
    'ctrl+/': actions.docs,
  });

  // If holding Alt, show hint
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey) {
        setShowAltHint(true);
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (!event.altKey) {
        setShowAltHint(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Live clock in the menu bar (updates every second)
  useEffect(() => {
    const id = setInterval(() => {
      setClockDisplay(formatTime(timeNow()));
      setHasClockOffset(getClockOffset() !== 0);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <Menubar.Root
        className={clsx(
          'menubar-root sticky top-0 z-10',
          showAltHint && 'menubar-show-hint',
        )}
        value={openMenu}
        onValueChange={setOpenMenu}
      >
        <Menubar.Menu value="file">
          <Menubar.Trigger className="menubar-trigger" data-menu="file">
            <span className="menubar-underline">F</span>ile
          </Menubar.Trigger>
          <Menubar.Portal>
            <Menubar.Content className="menubar-content" align="start">
              <Menubar.Item className="menubar-item" onSelect={actions.newFile}>
                New
              </Menubar.Item>
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.open}
                {...remoteConnectedDisabledProps}
              >
                Open
                <span className="menubar-shortcut">Ctrl+O</span>
              </Menubar.Item>
              <Menubar.Separator className="menubar-separator" />
              <Menubar.Item className="menubar-item" onSelect={actions.save}>
                Save
                <span className="menubar-shortcut">Ctrl+S</span>
              </Menubar.Item>
              <Menubar.Item className="menubar-item" onSelect={actions.saveAs}>
                Save As...
                <span className="menubar-shortcut">Ctrl+Shift+S</span>
              </Menubar.Item>
              <Menubar.Item className="menubar-item" onSelect={actions.export}>
                Export
                <span className="menubar-shortcut">Ctrl+E</span>
              </Menubar.Item>
              {IS_TAURI && (
                <Menubar.Item
                  className="menubar-item"
                  onSelect={actions.serverSettings}
                >
                  Server Settings
                </Menubar.Item>
              )}
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.connectServer}
                disabled={IS_TAURI && serverState !== undefined}
                title={
                  IS_TAURI && serverState !== undefined
                    ? 'Stop the local server before connecting'
                    : undefined
                }
              >
                Connect to Server
              </Menubar.Item>
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.disconnectServer}
                disabled={!isRemoteConnected}
              >
                Disconnect from Server
              </Menubar.Item>
              {(IS_TAURI ||
                window.opener !== null ||
                (IS_FILE && window.history.length === 1)) && (
                <>
                  <Menubar.Separator className="menubar-separator" />
                  <Menubar.Item
                    className="menubar-item"
                    onSelect={actions.exit}
                  >
                    Exit
                  </Menubar.Item>
                </>
              )}
            </Menubar.Content>
          </Menubar.Portal>
        </Menubar.Menu>

        <Menubar.Menu value="edit">
          <Menubar.Trigger className="menubar-trigger" data-menu="edit">
            <span className="menubar-underline">E</span>dit
          </Menubar.Trigger>
          <Menubar.Portal>
            <Menubar.Content className="menubar-content" align="start">
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.editReports}
                {...remoteConnectedDisabledProps}
              >
                Edit Reports
              </Menubar.Item>
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.editCodewords}
                {...remoteConnectedDisabledProps}
              >
                Edit Code Words
              </Menubar.Item>
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.editTags}
                {...remoteConnectedDisabledProps}
              >
                Edit Tags
              </Menubar.Item>
              <Menubar.Separator className="menubar-separator" />
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.editScheduledReminders}
                {...remoteConnectedDisabledProps}
              >
                Manage Scheduled Reminders
              </Menubar.Item>
              <Menubar.Separator className="menubar-separator" />
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.editClock}
              >
                Adjust Clock
              </Menubar.Item>
            </Menubar.Content>
          </Menubar.Portal>
        </Menubar.Menu>

        <Menubar.Menu value="view">
          <Menubar.Trigger className="menubar-trigger" data-menu="view">
            <span className="menubar-underline">V</span>iew
          </Menubar.Trigger>
          <Menubar.Portal>
            <Menubar.Content className="menubar-content" align="start">
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.newWindow}
              >
                New Window
                <span className="menubar-shortcut">Ctrl+Shift+N</span>
              </Menubar.Item>
              <Menubar.Item
                className="menubar-item"
                onSelect={actions.newOfficerWindow}
              >
                New Officer Window
              </Menubar.Item>
              <Menubar.Separator className="menubar-separator" />
              <Menubar.CheckboxItem
                className="menubar-item"
                checked={view === 'operator'}
                onCheckedChange={() => {
                  if (!isRemoteConnected) {
                    $view.set('operator');
                  }
                }}
                disabled={isRemoteConnected}
              >
                Operator View
                {view === 'operator' && (
                  <Menubar.ItemIndicator className="menubar-indicator">
                    ✓
                  </Menubar.ItemIndicator>
                )}
              </Menubar.CheckboxItem>
              <Menubar.CheckboxItem
                className="menubar-item"
                checked={view === 'officer'}
                onCheckedChange={() => $view.set('officer')}
              >
                Officer View
                {view === 'officer' && (
                  <Menubar.ItemIndicator className="menubar-indicator">
                    ✓
                  </Menubar.ItemIndicator>
                )}
              </Menubar.CheckboxItem>
              <Menubar.CheckboxItem
                className="menubar-item"
                checked={view === 'audit'}
                onCheckedChange={() => {
                  if (!isRemoteConnected) {
                    $view.set('audit');
                  }
                }}
                disabled={isRemoteConnected}
              >
                Audit View
                {view === 'audit' && (
                  <Menubar.ItemIndicator className="menubar-indicator">
                    ✓
                  </Menubar.ItemIndicator>
                )}
              </Menubar.CheckboxItem>
            </Menubar.Content>
          </Menubar.Portal>
        </Menubar.Menu>

        <Menubar.Menu value="help">
          <Menubar.Trigger className="menubar-trigger" data-menu="help">
            <span className="menubar-underline">H</span>elp
          </Menubar.Trigger>
          <Menubar.Portal>
            <Menubar.Content className="menubar-content" align="start">
              <Menubar.Item className="menubar-item" onSelect={actions.docs}>
                Documentation
                <span className="menubar-shortcut">Ctrl+/</span>
              </Menubar.Item>
              <Menubar.Item className="menubar-item" onSelect={actions.about}>
                About
              </Menubar.Item>
              <Menubar.Separator className="menubar-separator" />
              <Menubar.Item
                className="menubar-item"
                onSelect={() => openDialog('downloadDesktop')}
              >
                Download Desktop App
              </Menubar.Item>
            </Menubar.Content>
          </Menubar.Portal>
        </Menubar.Menu>
        {view === 'operator' && (
          <Menubar.Menu value="tutorial">
            <Menubar.Trigger
              className="menubar-trigger"
              data-menu="tutorial"
              onClick={() => $tutorial.set(true)}
            >
              <span className="menubar-underline">T</span>utorial
            </Menubar.Trigger>
          </Menubar.Menu>
        )}
        <Menubar.Menu value="feedback">
          <Menubar.Trigger
            className="menubar-trigger"
            style={{ padding: 0 }}
            data-menu="feedback"
          >
            <a
              href="https://forms.office.com/r/pdNCnfc1wn"
              target="_blank"
              rel="noopener noreferrer"
              className="block h-full w-full px-3 py-1"
            >
              Feedback
            </a>
          </Menubar.Trigger>
        </Menubar.Menu>
        <button
          className={clsx(
            'menubar-trigger ml-auto cursor-pointer font-mono text-sm select-none',
            hasClockOffset && 'text-yellow-700',
          )}
          onClick={actions.editClock}
          title={
            hasClockOffset
              ? 'Clock adjusted — click to modify'
              : 'Click to adjust clock'
          }
        >
          {clockDisplay}
          {hasClockOffset && ' ⚠'}
        </button>
      </Menubar.Root>
    </>
  );
}
